// src/lib/youtube-auth.ts
import { supabase } from './supabaseClient'

/**
 * Exchanges the YouTube OAuth code for access + refresh tokens via your backend.
 * Requires the current user's Supabase access token (JWT) for authorization.
 */
export async function exchangeYoutubeCode(code: string, accessToken: string, state?: string) {
  const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002'
  
  console.log('[exchangeYoutubeCode] 🔍 Backend URL:', backendUrl)
  console.log('[exchangeYoutubeCode] 🔍 Code length:', code?.length || 0)
  console.log('[exchangeYoutubeCode] 🔍 Access token present:', !!accessToken)
  console.log('[exchangeYoutubeCode] 🔍 State parameter:', state ? 'present' : 'missing')

  try {
    const response = await fetch(`${backendUrl}/youtube-auth`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        code,
        state // Pass the state parameter to backend
      }),
    })

    console.log('[exchangeYoutubeCode] 🔍 Response status:', response.status)
    console.log('[exchangeYoutubeCode] 🔍 Response headers:', Object.fromEntries(response.headers.entries()))

    if (!response.ok) {
      let errorDetails = { status: response.status, statusText: response.statusText }
      
      try {
        const errorData = await response.json()
        errorDetails = { ...errorDetails, ...errorData }
        console.error('[exchangeYoutubeCode] ❌ Server error details:', errorDetails)
      } catch (parseError) {
        // If JSON parsing fails, try text
        try {
          const errorText = await response.text()
          console.error('[exchangeYoutubeCode] ❌ Server error text:', errorText)
          errorDetails = { ...errorDetails, error: errorText }
        } catch {
          console.error('[exchangeYoutubeCode] ❌ Could not parse error response')
        }
      }
      
      throw new Error(`Backend auth failed (${response.status}): ${JSON.stringify(errorDetails)}`)
    }

    const result = await response.json()
    console.log('[exchangeYoutubeCode] ✅ Exchange successful:', { success: result.success })
    return result
    
  } catch (err: any) {
    console.error('[exchangeYoutubeCode] ❌ Network or server error:', err)
    
    // Provide more specific error messages
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      throw new Error(`Cannot connect to backend at ${backendUrl}. Please check your internet connection.`)
    }
    
    if (err.message.includes('Backend auth failed')) {
      throw err // Re-throw backend errors as-is
    }
    
    throw new Error(`YouTube authentication failed: ${err.message}`)
  }
}

/**
 * UPDATED: Initiates YouTube OAuth by redirecting to your backend's GET endpoint
 * This replaces the old getYouTubeAuthUrl function and handles environment detection automatically
 */
export function initiateYouTubeAuth(): void {
  const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002'
  
  console.log('[initiateYouTubeAuth] 🔍 Backend URL:', backendUrl)
  console.log('[initiateYouTubeAuth] 🔍 Current origin:', window.location.origin)
  
  // Redirect to the GET endpoint which will handle environment detection
  window.location.href = `${backendUrl}/youtube-auth`
}

/**
 * DEPRECATED: This function is no longer needed since the backend handles URL generation
 * Keeping it for backward compatibility but recommend using initiateYouTubeAuth instead
 */
export function getYouTubeAuthUrl(userId: string): string {
  console.warn('[getYouTubeAuthUrl] ⚠️ This function is deprecated. Use initiateYouTubeAuth() instead.')
  
  const clientId = import.meta.env.VITE_YOUTUBE_CLIENT_ID
  const redirectUri = import.meta.env.VITE_YOUTUBE_REDIRECT_URI

  console.log('[getYouTubeAuthUrl] 🔍 Client ID:', clientId ? `${clientId.substring(0, 10)}...` : 'MISSING')
  console.log('[getYouTubeAuthUrl] 🔍 Redirect URI:', redirectUri)
  console.log('[getYouTubeAuthUrl] 🔍 User ID:', userId)

  if (!clientId || !redirectUri) {
    throw new Error('Missing YouTube client ID or redirect URI in environment variables.')
  }

  const scope = [
    'https://www.googleapis.com/auth/youtube.upload',
    'https://www.googleapis.com/auth/youtube.readonly',
  ].join(' ')

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    scope,
    state: userId, // optional for extra tracking
  })

  const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
  console.log('[getYouTubeAuthUrl] 🔍 Generated auth URL:', authUrl)
  
  return authUrl
}

/**
 * Checks if the user already has a YouTube token saved in Supabase.
 */
export async function checkHasYoutubeToken(userId: string): Promise<boolean> {
  console.log('[checkHasYoutubeToken] 🔍 Checking for user:', userId)
  
  try {
    const { data, error } = await supabase
      .from('youtube_tokens')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      console.error('[checkHasYoutubeToken] ❌ Supabase error:', error)
      return false
    }

    const hasToken = !!data
    console.log('[checkHasYoutubeToken] 🔍 Has token:', hasToken)
    return hasToken
    
  } catch (err) {
    console.error('[checkHasYoutubeToken] ❌ Unexpected error:', err)
    return false
  }
}